import React, { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { useParams } from "react-router-dom";

import { Lead as LeadProps } from "@/types";

import { leadsGetById, leadsCreateNote, leadUpdateAiCall, leadUpdate, leadsUpdateNote, leadsDeleteNote, leadsChangeLSAStatus, updateLeadStatus, markLeadMessagesAsRead } from "@/store/leads";
import { dialogsSendMessage, dialogSendGLSMessage } from "@/store/dialogs";
import { leadsCreateReminder, leadsDeleteReminder } from "@/store/leads";

import AppBox from "@/components/AppBox";
import AppText from "@/components/AppText";
import AppIcon from "@/components/AppIcon";
import AppLink from "@/components/AppLink";

import LeadInfo from "@/components/Lead/Info";
import LeadMessages from "@/components/Lead/Messages";
import LeadNotes from "@/components/Lead/Notes";
import AppRemindMe from "@/components/AppRemindMe";
import ChangeLeadStatus from "@/components/ChangeLeadStatus";
import StatusSelect from "@/components/Lead/StatusSelect";

const Lead: React.FC = () => {
  const dispatch = useAppDispatch();
  const { id: idParam } = useParams<{ id: string }>();

  const { lead } = useAppSelector((state: any) => state.leads);

  const [currentLead, setCurrentLead] = useState<LeadProps>(lead)

  useEffect(() => {
    if (idParam) {
      const id = +idParam
      handleFetchLead(id);
    }
  }, [idParam]);

  useEffect(() => {
    if (lead?.id) {
      setCurrentLead(lead)
      
      // Mark messages as read when viewing the lead
      if (lead.new) {
        dispatch(markLeadMessagesAsRead(lead.id));
      }
    }
  }, [lead]);

  const handleFetchLead = (leadId: number) => {
    dispatch(leadsGetById(leadId));
  };

  const handleSendMessage = (message: string) => {
    if (lead.type === 'Message') {
      dispatch(dialogSendGLSMessage({ id: lead.id, data: { text: message } }))
    } else {
      dispatch(dialogsSendMessage({id: lead.id, data: {text: message}}))
    }
  };

  const handleSubmitNote = (note: string) => {
    if (lead?.id) {
      dispatch(
        leadsCreateNote({ id: lead.id, note })
      );
    }
  };

  const handleChangeAiCall = (data: any) => {
    if (lead?.id) {
      dispatch(
        leadUpdateAiCall({ id: lead.aiCall.id, data })
      );
    }
  };

  const handleChangeLead = (data: any) => {
    dispatch(leadUpdate({id: lead.id, data}))
  }

  const handleUpdateNote = (id: number, note: string) => {
    dispatch(leadsUpdateNote({id, note}))
  }
  
  const handleDeleteNote = (id: number) => {
    if (window.confirm(`Are you sure you want to delete this note?`)) {
      dispatch(leadsDeleteNote({id}))
    }
  }
  
  const handleSubmitReminder = (delay: any) => {
    dispatch(leadsCreateReminder({id: lead.id, data: {delay}})) // set reminder in 24h
  }
  
  const handleDeleteReminder = () => {
    dispatch(leadsDeleteReminder(lead.id)) // set reminder in 24h
  }

  const handleChangeLSAStatus = (status: string) => {
    if (!lead?.id) return;

    dispatch(leadsChangeLSAStatus({id: lead.id, data: {status}}))
      .unwrap()
      .then(() => {
        dispatch(updateLeadStatus({ 
          leadId: lead.id, 
          status 
        }));
      })
      .catch((error) => {
        console.error('Failed to update LSA status:', error);
      });
  }

  if (!lead) {
    return null;
  }

  return (
    <AppBox column fullHeight gap={2} fullWidth>
      {/* Header */}
      <AppBox align="center" gap={4}>
        <AppLink to="/">
          <AppIcon name="chevronLeft" />
          Back
        </AppLink>
        <AppText variant="h3">
          {`${currentLead?.name || `LSA ${currentLead?.type} Lead`}`}
        </AppText>
      </AppBox>

      {/* Main Content */}
      <AppBox column gap={4} className="h-[calc(100vh-140px)] lg:flex-row">
        {/* Lead Info */}
        <AppBox fullHeight flex={0.5} className="hidden lg:flex">
          <LeadInfo lead={currentLead} onAiCallChange={handleChangeAiCall} onChange={handleChangeLead} />
        </AppBox>

        {/* Lead Messages */}
        <AppBox fullHeight flex={1} className="lg:flex">
          <LeadMessages messages={currentLead?.messages} onSubmit={handleSendMessage} />
        </AppBox>

        {/* Lead Notes */}
        <AppBox column fullHeight flex={0.4} gap={4} className="hidden lg:flex">
          <AppRemindMe onSubmit={handleSubmitReminder} lead={lead} onDelete={handleDeleteReminder} />
          <ChangeLeadStatus lead={lead} onStatusChange={handleChangeLSAStatus} />
          <LeadNotes notes={currentLead?.notes} onSubmit={handleSubmitNote} onUpdateNote={handleUpdateNote} onDeleteNote={handleDeleteNote} />
        </AppBox>

        {/* Adjust layout for small screens */}
        <AppBox
          gap={4}
          fullHeight
          className="lg:hidden flex-col md:flex-row"
        >
          <AppBox flex={0.5}>
            <LeadInfo lead={currentLead} onAiCallChange={handleChangeAiCall} onChange={handleChangeLead} />
          </AppBox>

          <AppBox column flex={0.5} className="pb-8" gap={4}>
            <AppRemindMe onSubmit={handleSubmitReminder} lead={lead} onDelete={handleDeleteReminder} />
            <ChangeLeadStatus lead={lead} onStatusChange={handleChangeLSAStatus} />
            <LeadNotes notes={currentLead?.notes} onSubmit={handleSubmitNote} onUpdateNote={handleUpdateNote} onDeleteNote={handleDeleteNote} />
          </AppBox>
        </AppBox>
      </AppBox>
    </AppBox>
  );
};

export default Lead;